export type BgmMode = "opening" | "play";
export type SoundEffect = "move" | "hit" | "pause" | "erase" | "gameOver" | "button";

interface AudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const BGM_FILES: Record<BgmMode, string> = {
  opening: "/audio/friel-opening.opus",
  play: "/audio/friel-maintheme.opus"
};

const SE_CUES_URL = "/audio/se-cues.json";

interface SeCue {
  start: number;
  duration: number;
  gain?: number;
}

interface SeSpriteManifest {
  sprite: {
    file: string;
  };
  cues: Partial<Record<SoundEffect, SeCue>>;
}

export class AudioEngine {
  private context: AudioContext | null = null;
  private bgmMode: BgmMode | null = null;
  private currentBgm: HTMLAudioElement | null = null;
  private bgmElements = new Map<BgmMode, HTMLAudioElement>();
  private bgmEnabled = true;
  private bgmMuted = false;
  private seEnabled = true;
  private bgmVolume = 0.7;
  private seVolume = 0.7;
  private seSpriteBuffer: AudioBuffer | null = null;
  private seCues = new Map<SoundEffect, SeCue>();
  private seLoadPromise: Promise<void> | null = null;
  private activeSources = new Set<AudioScheduledSourceNode>();

  setEnabled(options: { bgm: boolean; se: boolean; bgmVolume: number; seVolume: number }): void {
    this.bgmEnabled = options.bgm;
    this.seEnabled = options.se;
    this.bgmVolume = volumeScale(options.bgmVolume);
    this.seVolume = volumeScale(options.seVolume);
    for (const element of this.bgmElements.values()) {
      element.volume = this.bgmVolume;
    }
    if (!this.bgmEnabled) {
      this.stopBgm();
    }
  }

  suspendForLifecycle(): void {
    this.currentBgm?.pause();
    this.stopActiveSources();
    if (this.context?.state === "running") {
      void this.context.suspend().catch(() => undefined);
    }
  }

  resumeForLifecycle(): void {
    if (this.context?.state === "suspended") {
      void this.context.resume().catch(() => undefined);
    }
  }

  async unlock(): Promise<void> {
    const context = this.ensureContext();
    if (context && context.state === "suspended") {
      await context.resume();
    }
    if (context) {
      await this.loadSoundEffects(context);
    }
    if (this.currentBgm && this.bgmEnabled && !this.bgmMuted) {
      await this.currentBgm.play().catch(() => undefined);
    }
  }

  startBgm(mode: BgmMode): void {
    if (!this.bgmEnabled) {
      this.stopBgm();
      return;
    }
    this.bgmMode = mode;
    const element = this.getBgmElement(mode);
    if (!element) {
      return;
    }
    if (this.currentBgm && this.currentBgm !== element) {
      this.currentBgm.pause();
      element.currentTime = 0;
    }
    this.currentBgm = element;
    element.volume = this.bgmVolume;
    if (this.bgmMuted) {
      element.pause();
      return;
    }
    void element.play().catch(() => undefined);
  }

  restartBgm(mode: BgmMode): void {
    const element = this.getBgmElement(mode);
    if (!element) {
      return;
    }
    if (this.currentBgm && this.currentBgm !== element) {
      this.currentBgm.pause();
    }
    element.currentTime = 0;
    this.currentBgm = element;
    this.bgmMode = mode;
    element.volume = this.bgmVolume;
    if (this.bgmEnabled && !this.bgmMuted) {
      void element.play().catch(() => undefined);
    }
  }

  stopBgm(): void {
    if (this.currentBgm) {
      this.currentBgm.pause();
      this.currentBgm = null;
    }
    this.bgmMode = null;
  }

  setBgmMuted(muted: boolean): void {
    this.bgmMuted = muted;
    if (muted) {
      this.currentBgm?.pause();
      return;
    }
    if (this.bgmMode) {
      this.startBgm(this.bgmMode);
    }
  }

  playSe(effect: SoundEffect): void {
    if (!this.seEnabled) {
      return;
    }
    const context = this.ensureContext();
    if (!context) {
      return;
    }
    void this.loadSoundEffects(context);

    if (this.playSpriteSe(context, effect)) {
      return;
    }

    switch (effect) {
      case "move":
        this.tone(220, 0.018, "square", 0.035, 0, this.seVolume);
        break;
      case "hit":
        this.tone(170, 0.055, "triangle", 0.06, 0, this.seVolume);
        this.tone(120, 0.05, "sine", 0.035, 0.04, this.seVolume);
        break;
      case "pause":
        this.tone(440, 0.055, "triangle", 0.04, 0, this.seVolume);
        this.tone(330, 0.055, "triangle", 0.03, 0.055, this.seVolume);
        break;
      case "erase":
        this.tone(660, 0.11, "sawtooth", 0.055, 0, this.seVolume);
        this.tone(990, 0.09, "square", 0.035, 0.04, this.seVolume);
        break;
      case "gameOver":
        this.tone(160, 0.3, "sine", 0.055, 0, this.seVolume);
        this.tone(110, 0.36, "triangle", 0.04, 0.12, this.seVolume);
        break;
      case "button":
        this.tone(920, 0.022, "square", 0.035, 0, this.seVolume);
        break;
    }
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    delay = 0,
    outputScale = 1
  ): void {
    const context = this.ensureContext();
    if (!context) {
      return;
    }
    const oscillator = this.trackSource(context.createOscillator());
    const gain = context.createGain();
    const start = context.currentTime + delay;
    const end = start + duration;
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume * outputScale), start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }

  private trackSource<T extends AudioScheduledSourceNode>(source: T): T {
    this.activeSources.add(source);
    source.addEventListener("ended", () => this.activeSources.delete(source), { once: true });
    return source;
  }

  private stopActiveSources(): void {
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        /* The source may already have stopped. */
      }
    }
    this.activeSources.clear();
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === "undefined") {
      return null;
    }
    if (!this.context) {
      const AudioContextCtor = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
      if (!AudioContextCtor) {
        return null;
      }
      this.context = new AudioContextCtor();
    }
    return this.context;
  }

  private getBgmElement(mode: BgmMode): HTMLAudioElement | null {
    if (typeof Audio === "undefined") {
      return null;
    }
    const existing = this.bgmElements.get(mode);
    if (existing) {
      return existing;
    }
    const element = new Audio(BGM_FILES[mode]);
    element.loop = true;
    element.preload = "auto";
    element.volume = this.bgmVolume;
    this.bgmElements.set(mode, element);
    return element;
  }

  private async loadSoundEffects(context: AudioContext): Promise<void> {
    if (this.seLoadPromise) {
      return this.seLoadPromise;
    }

    this.seLoadPromise = fetch(SE_CUES_URL)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to load ${SE_CUES_URL}`);
        }
        const manifest = (await response.json()) as SeSpriteManifest;
        const spriteResponse = await fetch(manifest.sprite.file);
        if (!spriteResponse.ok) {
          throw new Error(`Unable to load ${manifest.sprite.file}`);
        }
        const bytes = await spriteResponse.arrayBuffer();
        const buffer = await context.decodeAudioData(bytes.slice(0));
        this.seSpriteBuffer = buffer;
        this.seCues.clear();
        for (const [effect, cue] of Object.entries(manifest.cues)) {
          if (cue) {
            this.seCues.set(effect as SoundEffect, cue);
          }
        }
      })
      .catch(() => {
        this.seSpriteBuffer = null;
        this.seCues.clear();
      });

    return this.seLoadPromise;
  }

  private playSpriteSe(context: AudioContext, effect: SoundEffect): boolean {
    const cue = this.seCues.get(effect);
    if (!this.seSpriteBuffer || !cue) {
      return false;
    }

    const source = this.trackSource(context.createBufferSource());
    const gain = context.createGain();
    source.buffer = this.seSpriteBuffer;
    gain.gain.value = (cue.gain ?? 0.8) * this.seVolume;
    source.connect(gain);
    gain.connect(context.destination);
    source.start(0, cue.start, cue.duration);
    return true;
  }
}

function volumeScale(value: number): number {
  if (!Number.isFinite(value)) {
    return 0.7;
  }
  return Math.max(0, Math.min(1, value / 100));
}
